// Layer 2 (Engine): the Operator's recorded rounds this session (polish.P16
// round 1, #703). The Record & submit card on the Operator landing enters one
// value per reading for the round a RECORD task names (`op-r-manual12`, the
// 12:00 manual readings) and submits it at the session cursor. The entries and
// the submission are held by `OperatorRoundProvider` at the app root beside
// `EscalationDecisionProvider`, so a round outlives the card's mount: a switch
// to another landing view, or to another persona and back, keeps both; only a
// reload resets them. Like the escalation decisions, a submission is written
// once — a later submit never moves it — unless the session clock is
// stepped back before it, where it has not happened yet.

import type { SeriesTime } from '@/versions/v1/schema/reading-series';

export type OperatorRoundRecord = {
  /** The RECORD task the round completes. */
  taskId: string;
  /** The value typed per reading row id (`rd-1` … `rd-8`), as entered. */
  entries: Readonly<Record<string, string>>;
  /** When the round was submitted; null while it is still being entered. */
  submittedAt: SeriesTime | null;
};

export type OperatorRoundState = ReadonlyMap<string, OperatorRoundRecord>;

export const INITIAL_OPERATOR_ROUNDS: OperatorRoundState = new Map();

function recordFor(state: OperatorRoundState, taskId: string): OperatorRoundRecord {
  return state.get(taskId) ?? { taskId, entries: {}, submittedAt: null };
}

/** Returns the state with one reading's entry set; a round submitted by the cursor is closed to edits. */
export function enterRoundValue(
  state: OperatorRoundState,
  taskId: string,
  rowId: string,
  value: string,
  cursor: SeriesTime,
): OperatorRoundState {
  if (roundSubmittedAt(state, taskId, cursor) !== null) return state;
  const current = recordFor(state, taskId);
  const next = new Map(state);
  next.set(taskId, { ...current, entries: { ...current.entries, [rowId]: value } });
  return next;
}

/**
 * Returns the state with the round submitted at `at`. A round the cursor
 * already sees submitted keeps its first time; a submission after the cursor
 * has not happened yet at `at`, so submitting again replaces it.
 */
export function submitRound(state: OperatorRoundState, taskId: string, at: SeriesTime): OperatorRoundState {
  if (roundSubmittedAt(state, taskId, at) !== null) return state;
  const next = new Map(state);
  next.set(taskId, { ...recordFor(state, taskId), submittedAt: at });
  return next;
}

/**
 * When the round was submitted, as the cursor sees it: only a submission on
 * the cursor's own day and at or before the cursor has happened yet, so
 * stepping the session clock back before the submit reads the task open again.
 */
export function roundSubmittedAt(
  state: OperatorRoundState,
  taskId: string,
  cursor: SeriesTime,
): SeriesTime | null {
  const at = state.get(taskId)?.submittedAt ?? null;
  if (at === null) return null;
  return at.slice(0, 10) === cursor.slice(0, 10) && at <= cursor ? at : null;
}
